import { useState, useEffect, useCallback, useRef } from 'react';
import { Menu, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface NavbarProps {
  onContactClick: () => void;
}

export default function Navbar({ onContactClick }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setScrolled(window.scrollY > 20);
      }, 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const navLinks = [
    { name: '关于我们', href: '#about' },
    { name: '解决方案', href: '#solution' },
    { name: '产品服务', href: '#products' },
    { name: '合作流程', href: '#process' },
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/90 backdrop-blur-xl py-4 border-b border-primary-100 shadow-sm' 
          : 'bg-white/50 backdrop-blur-sm py-6'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span className="text-2xl font-bold tracking-tight font-sans text-primary-900 hover:text-primary-600 transition-colors">
            Omnisonic <span className="text-primary-600">AI</span>
          </span>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-10">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-gray-600 hover:text-primary-600 transition-colors text-sm font-medium tracking-wide"
            >
              {link.name}
            </a>
          ))}
        </div>

        <div className="hidden md:block relative">
          <button 
            onClick={onContactClick}
            onMouseEnter={() => setShowQR(true)}
            onMouseLeave={() => setShowQR(false)}
            className="px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 transform hover:scale-105 bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/20 inline-flex items-center gap-2"
          >
            <span className="w-8 h-5 rounded-full bg-white/20 inline-flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-white" />
            </span>
            Get Started
          </button>

          {/* QR Popup on Hover */}
          <AnimatePresence>
            {showQR && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50"
                onMouseEnter={() => setShowQR(true)}
                onMouseLeave={() => setShowQR(false)}
              >
                <p className="text-sm font-semibold text-gray-900 mb-3 text-center">扫码联系客服</p>
                <div className="w-full aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                  <img 
                    src="/wechat-qr.png" 
                    alt="企业微信客服" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">使用微信扫描</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="p-2 rounded-lg transition-colors text-primary-900 hover:bg-primary-50"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-primary-100 overflow-hidden absolute top-full left-0 w-full shadow-2xl"
          >
            <div className="flex flex-col p-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-gray-800 hover:text-primary-600 font-medium text-lg py-2 border-b border-gray-100 last:border-0"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <button 
                onClick={() => {
                  onContactClick();
                  setIsOpen(false);
                }}
                className="w-full py-3 mt-4 rounded-xl bg-primary-600 text-white font-bold shadow-lg shadow-primary-600/20"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
